import { memo, useCallback, useEffect, useState } from 'react'
import styles from './index.module.scss'
import { Button, Modal } from 'antd'
import { Player } from 'types/players'
import { createPlayer, deletePlayer, editPlayer, getPlayerById, getPlayers } from 'services/players'
import { PlayersTable } from '../../components/players-table'
import { PlayerForm } from '../../components/player-form'

export const Players = memo(() => {
    const [players, setPlayers] = useState<Player[]>([])
    const [open, setOpen] = useState<boolean>(false)
    const [player, setPlayer] = useState<Player>({} as Player)
    const [loading, setLoading] = useState<boolean>(false)
    const [dataIsFetching, setDataIsFetching] = useState<boolean>(false)

    const getAllData = useCallback(() => {
        setDataIsFetching(true)

        const getAll = async function () {
            return await getPlayers()
        }

        getAll()
            .then(data => {
                setPlayers(data)
            })
            .finally(() => {
                setDataIsFetching(false)
            })
    }, [])

    useEffect(() => {
        getAllData()
    }, [getAllData])

    const toggleModal = useCallback(() => {
        setOpen(!open)
    }, [open])

    const handleAdd = useCallback(() => {
        setPlayer({} as Player)
        toggleModal()
    }, [toggleModal])

    const handleEdit = useCallback(
        (id: number) => {
            setLoading(true)
            getPlayerById(id)
                .then(item => {
                    setPlayer(item)
                    toggleModal()
                })
                .finally(() => {
                    setLoading(false)
                })
        },
        [toggleModal]
    )

    const handleDelete = useCallback(
        (id: number) => {
            setLoading(true)
            deletePlayer(id)
                .then(getAllData)
                .finally(() => {
                    setLoading(false)
                })
        },
        [getAllData]
    )

    const handleFormSubmit = useCallback(
        (submitValues: Player) => {
            setLoading(true)

            const refetchDataAndCloseModal = () => {
                getAllData()
                toggleModal()
            }

            if (player.id) {
                editPlayer({
                    ...submitValues,
                    id: player.id,
                })
                    .then(refetchDataAndCloseModal)
                    .finally(() => {
                        setLoading(false)
                    })
            } else {
                createPlayer(submitValues)
                    .then(refetchDataAndCloseModal)
                    .finally(() => {
                        setLoading(false)
                    })
            }
        },
        [getAllData, player, toggleModal]
    )

    const editMode = !!player.id
    const modalTitle = `${editMode ? `Edit player ${player.surname} ${player.name}` : `Add new player`}`

    const somethingLoading = loading || dataIsFetching

    return (
        <div>
            Players
            <div className={styles.actions}>
                <Button type={'primary'} onClick={handleAdd}>
                    Add
                </Button>
            </div>
            <PlayersTable data={players} onEdit={handleEdit} onDelete={handleDelete} loading={somethingLoading} />
            <Modal
                title={modalTitle}
                centered
                open={open}
                width={1000}
                destroyOnClose
                footer={null}
                onCancel={toggleModal}
                maskClosable={false}
            >
                <PlayerForm player={player} onSubmit={handleFormSubmit} loading={somethingLoading} />
            </Modal>
        </div>
    )
})
